import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../components/label/MoneyLabel';
import ProjectControllingBars from './ProjectControllingBars';

const toNumber = (value) => {
  return Number(value || 0);
};

const secondsToHours = (seconds) => {
  return Math.round(toNumber(seconds) / 3600);
};

const ProjectControllingDetailsCard = ({
  data,
  visibleMetrics = [],
  isLoading,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={130}
        sx={{
          borderRadius: '12px',
        }}
      />
    );
  }

  if (!data) {
    return null;
  }

  // Values come directly from projects/controlling/accumulated.
  const totalProjects = toNumber(data.totalProjects);
  const totalOrderedSumNetto = toNumber(data.totalOrderedSumNetto);
  const totalInvoicedSumNetto = toNumber(data.totalInvoicedSumNetto);

  const totalTimePlannedSeconds = toNumber(data.totalTimePlannedSeconds);
  const totalTimeWorkedSeconds = toNumber(data.totalTimeWorkedSeconds);

  const totalWorkPackageProgress = toNumber(data.totalWorkPackageProgress);

  const totalOpenBillableTimeSeconds = toNumber(
    data.totalOpenBillableTimeSeconds,
  );
  const totalOpenBillableTimeNetto = toNumber(data.totalOpenBillableTimeNetto);

  // Remaining volume is calculated from accumulated ordered and invoiced amounts.
  const remainingVolume = totalOrderedSumNetto - totalInvoicedSumNetto;

  // Same logic as billableByProgress, but using accumulated API values.
  const billingProgress =
    totalOrderedSumNetto > 0
      ? (totalInvoicedSumNetto / totalOrderedSumNetto) * 100
      : 0;

  const billableByProgressAmount =
    totalOrderedSumNetto > 0
      ? ((totalWorkPackageProgress - billingProgress) / 100) *
        totalOrderedSumNetto
      : 0;

  // Normalize accumulated API data so ProjectControllingBars can be reused.
  const overviewBarRow = {
    workPackageProgress: totalWorkPackageProgress,
    orderedSumNetto: totalOrderedSumNetto,
    invoicedSumNetto: totalInvoicedSumNetto,
    totalTimePlannedSeconds,
    totalTimeWorkedSeconds,
  };

  const metricItems = {
    orderVolume: {
      label: t('Order Volume'),
      value: <MoneyLabel money={totalOrderedSumNetto} />,
    },
    invoiced: {
      label: t('Invoiced'),
      value: <MoneyLabel money={totalInvoicedSumNetto} />,
    },
    openBillable: {
      label: t('Time Billing (open)'),
      value: <MoneyLabel money={totalOpenBillableTimeNetto} />,
      color:
        totalOpenBillableTimeNetto > 0
          ? colors.money.positive
          : colors.money.neutral,
    },
    billableByProgress: {
      label: t('Performance Billing (open)'),
      value:
        totalOrderedSumNetto > 0 ? (
          <MoneyLabel money={billableByProgressAmount} />
        ) : (
          '–'
        ),
      color:
        billableByProgressAmount > 0
          ? colors.money.positive
          : billableByProgressAmount < 0
            ? colors.money.negative
            : colors.money.neutral,
    },
    openBillableHours: {
      label: t('Open Billable Hours'),
      value: `${secondsToHours(totalOpenBillableTimeSeconds)} h`,
      color:
        totalOpenBillableTimeSeconds > 0
          ? colors.money.positive
          : colors.money.neutral,
    },
    remainingVolume: {
      label: t('Remaining Volume'),
      value: <MoneyLabel money={remainingVolume} />,
      color:
        remainingVolume < 0
          ? colors.money.negative
          : remainingVolume > 0
            ? colors.money.positive
            : colors.money.neutral,
    },
  };

  // Only show metrics that are enabled in the display settings popover.
  const visibleSummaryItems = visibleMetrics
    .map((key) => metricItems[key])
    .filter(Boolean);

  return (
    <Box
      sx={{
        backgroundColor: colors.glass,
        borderRadius: '12px',
        backdropFilter: 'blur(12px)',
      }}
      className="p-3"
    >
      <Box className="flex flex-col gap-3">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('Overview')}
          </Typography>
        </Box>

        <Box className="flex flex-col lg:flex-row gap-4">
          <Box className="flex flex-wrap gap-4 items-center flex-1">
            <Box className="flex flex-col">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                }}
              >
                {t('Projects')}
              </Typography>

              <Typography
                variant="body2"
                className="monospace"
                fontWeight="bold"
                sx={{
                  fontSize: '0.85rem',
                }}
              >
                {totalProjects}
              </Typography>
            </Box>

            {visibleSummaryItems.map((item) => (
              <Box key={item.label} className="flex flex-col">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="body2"
                  className="monospace"
                  fontWeight="bold"
                  sx={{
                    color: item.color || 'inherit',
                    fontSize: '0.85rem',
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ flex: 1, minWidth: 260 }}>
            <ProjectControllingBars row={overviewBarRow} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectControllingDetailsCard;
